'use client';

import { queryOptions, useQuery } from '@tanstack/react-query';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import {
  fetchProductsWithSignal,
  productsListQueryKey,
  retryProductsQuery,
} from '@eliadmin/core/src/api/products/products-query';
import {
  categoryProductsQueryOptions,
  type CategoryFilterState,
} from '@eliadmin/core/src/components/categories/category-query-state';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';

/** The category listing request for `filter` with one product per page – `meta.total` is the count. */
export function productCountQueryOptions({
  locale,
  authToken,
  categoryId,
  filter,
}: {
  locale: string;
  authToken?: string;
  categoryId: number;
  filter: CategoryFilterState;
}) {
  const query = buildProductsQuery(categoryProductsQueryOptions(categoryId, 1, filter));
  return queryOptions({
    queryKey: productsListQueryKey(locale, buildProductsRequest(query).uri, authToken),
    queryFn: ({ signal }) => fetchProductsWithSignal({ query, locale, authToken, signal }),
    retry: retryProductsQuery,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

/**
 * Number of products the pending drawer selection would show: one request per settled selection
 * (the API has no facet counts). `total` is undefined while unknown or when the request failed.
 */
export function useProductCount(categoryId: number, filter: CategoryFilterState, enabled: boolean) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const isInitialized = useCustomerStore(s => s.isInitialized);
  const settled = useDebounce(filter, 300);

  const { data, isFetching } = useQuery({
    ...productCountQueryOptions({ locale, authToken, categoryId, filter: settled }),
    // Like the listing, wait until the customer is known – the request carries their token.
    enabled: enabled && isInitialized !== false,
    select: response => response.meta.total,
  });

  return { total: data, counting: enabled && (settled !== filter || isFetching) };
}
